import React, { useState, useEffect } from 'react';
import { Upload, Avatar, Button, message, Modal, Spin } from 'antd';
import { UploadOutlined, UserOutlined, DeleteOutlined, EyeOutlined } from '@ant-design/icons';
import axios from 'axios';

const AvatarUpload = ({ userId, onAvatarChange, currentAvatarUrl, size = 120 }) => {
  const [uploading, setUploading] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(currentAvatarUrl);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  const token = localStorage.getItem('token');

  useEffect(() => {
    setAvatarUrl(currentAvatarUrl);
  }, [currentAvatarUrl]);

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('Можно загружать только изображения!');
      return false;
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('Размер файла должен быть меньше 5MB!');
      return false;
    }

    return true;
  };

  const handleUpload = async (file) => {
    if (!beforeUpload(file)) {
      return false;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await axios.post(
        `https://limitverify.ru:5000/api/avatar/users/${userId}/avatar`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const newAvatarUrl = response.data.avatarUrl;
        setAvatarUrl(newAvatarUrl);
        message.success('Аватарка успешно загружена!');
        
        // Вызываем callback для обновления родительского компонента
        if (onAvatarChange) {
          onAvatarChange(newAvatarUrl);
        }
      } else {
        message.error(response.data.error || 'Ошибка загрузки аватарки');
      }
    } catch (error) {
      console.error('Ошибка загрузки аватарки:', error);
      message.error(error.response?.data?.error || 'Ошибка загрузки аватарки');
    } finally {
      setUploading(false);
    }

    return false; // Предотвращаем автоматическую загрузку
  };

  const handleDelete = async () => {
    try {
      setUploading(true);
      
      const response = await axios.delete(
        `https://limitverify.ru:5000/api/avatar/users/${userId}/avatar`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        setAvatarUrl(null);
        message.success('Аватарка удалена!');
        
        // Вызываем callback для обновления родительского компонента
        if (onAvatarChange) {
          onAvatarChange(null);
        }
      } else {
        message.error(response.data.error || 'Ошибка удаления аватарки');
      }
    } catch (error) {
      console.error('Ошибка удаления аватарки:', error);
      message.error(error.response?.data?.error || 'Ошибка удаления аватарки');
    } finally {
      setUploading(false);
    }
  };

  const handlePreview = () => {
    setPreviewImage(avatarUrl);
    setPreviewVisible(true);
  };

  const uploadButton = (
    <div>
      <UploadOutlined />
      <div style={{ marginTop: 8 }}>Загрузить</div>
    </div>
  );

  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ marginBottom: 16 }}>
        <Avatar
          size={size}
          src={avatarUrl}
          icon={<UserOutlined />}
          style={{ cursor: 'pointer' }}
          onClick={avatarUrl ? handlePreview : undefined}
        />
      </div>

      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
        <Upload
          name="avatar"
          showUploadList={false}
          beforeUpload={handleUpload}
          disabled={uploading}
        >
          <Button 
            icon={<UploadOutlined />} 
            loading={uploading}
            disabled={uploading}
            size="small"
          >
            {uploading ? 'Загрузка...' : 'Загрузить'}
          </Button>
        </Upload>

        {avatarUrl && (
          <>
            <Button 
              icon={<EyeOutlined />} 
              onClick={handlePreview}
              disabled={uploading}
              size="small"
            >
              Просмотр
            </Button>
            <Button 
              icon={<DeleteOutlined />} 
              danger
              onClick={handleDelete}
              loading={uploading}
              disabled={uploading}
              size="small"
            >
              Удалить
            </Button>
          </>
        )}
      </div>

      <Modal
        open={previewVisible}
        title="Просмотр аватарки"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="Аватарка" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default AvatarUpload;
